import type { CartAttributionSource, CartInputDto } from '@cineamo/api/models/cart/CartInputDto';

/**
 * Where a guest came from, taken from the utm_* parameters of a link Cineamo (or a cinema) tagged, or from an ad
 * click. Kept in sessionStorage and sent once, when the cart is created. It is a first-party marketing parameter:
 * the API reads it only in aggregate per cinema and it carries no identifier of the person, so it needs no
 * analytics consent.
 */
export type StoredAttribution = { source: CartAttributionSource; campaign: string | null; at: number };

const STORAGE_KEY = 'cineamo-attribution';
const AD_CLICK_MAX_AGE_MS = 24 * 60 * 60 * 1000;
// Mirrors the API validator: [A-Za-z0-9_-], max 64. Anything else (an email pasted into a campaign, say) is dropped.
const CAMPAIGN_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
const SOCIAL_MEDIUMS = ['social', 'social-media', 'social_media', 'paid-social', 'paid_social', 'organic-social'];

/** `null` when the link carries no utm_* parameter at all, i.e. nothing to attribute. */
export function mapAttribution(searchParams: URLSearchParams, at: number): StoredAttribution | null {
    const medium = searchParams.get('utm_medium')?.trim().toLowerCase() ?? '';
    const utmSource = searchParams.get('utm_source')?.trim() ?? '';
    const campaign = searchParams.get('utm_campaign')?.trim() ?? '';
    if (!medium && !utmSource && !campaign) {
        return null;
    }

    let source: CartAttributionSource = 'other';
    if (medium === 'push') {
        source = 'push';
    } else if (medium === 'newsletter') {
        source = 'newsletter';
    } else if (medium === 'email') {
        source = 'email';
    } else if (SOCIAL_MEDIUMS.includes(medium)) {
        source = 'social';
    }

    return { source, campaign: CAMPAIGN_PATTERN.test(campaign) ? campaign : null, at };
}

/** Last tagged visit wins; a landing without utm_* leaves what is stored untouched. */
export function captureAttribution(search: string, now = Date.now()): void {
    const attribution = mapAttribution(new URLSearchParams(search), now);
    if (!attribution) {
        return;
    }
    try {
        const previous = readStored();
        if (previous && previous.source === attribution.source && previous.campaign === attribution.campaign) {
            return; // same landing seen again: keep its original time
        }
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
    } catch {
        // storage blocked: attribution is best-effort
    }
}

function readStored(): StoredAttribution | null {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        return stored ? (JSON.parse(stored) as StoredAttribution) : null;
    } catch {
        return null;
    }
}

function readAdClickAt(contentId?: number | string): number | null {
    if (!contentId) {
        return null;
    }
    try {
        // Written by storeAdClickAttribution; consumed later by the purchase intent, so only peeked at here.
        const stored = sessionStorage.getItem(`adClick-${contentId}`);
        const clickedAt = stored ? (JSON.parse(stored) as { clickedAt?: number }).clickedAt : undefined;
        return clickedAt && Date.now() - clickedAt <= AD_CLICK_MAX_AGE_MS ? clickedAt : null;
    } catch {
        return null;
    }
}

/** The cart fields to send on creation. An ad click newer than the last tagged link wins. */
export function getCartAttribution(
    contentId?: number | string
): Pick<CartInputDto, 'attributionSource' | 'attributionCampaign'> {
    // The cart can be created before AttributionCapture's effect has run (children's effects go first), so the
    // landing URL is read here as well; it is idempotent.
    if (typeof window !== 'undefined') {
        captureAttribution(window.location.search);
    }
    const stored = readStored();
    const adClickAt = readAdClickAt(contentId);
    if (adClickAt !== null && (!stored || adClickAt > stored.at)) {
        return { attributionSource: 'ad' };
    }
    if (!stored) {
        return {};
    }
    return {
        attributionSource: stored.source,
        ...(stored.campaign ? { attributionCampaign: stored.campaign } : {})
    };
}
